import { BranchResponse } from '@/app/models/branch';
import { GeneratedReportRequest } from '@/app/models/generatedReport';
import { PaginationResponse } from '@/app/models/pagination';
import { ProjectResponse } from '@/app/models/projects';
import { ProjectSubTypeResponse } from '@/app/models/projectSubTypes';
import { useBranchsQuery } from '@/app/services/branchApi';
import { useExportProjectMutation, useSyncPlanningReportMutation } from '@/app/services/generatedReportApi';
import { useProjectsQuery } from '@/app/services/projectsApi';
import { useProjectSubTypeQuery } from '@/app/services/projectsSubTypeApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import GenerateReportScreen from '@/components/project/generateReport/GenerateReportScreen';
import NewProjectsSettingsScreen from '@/components/project/NewProjectsSettingsScreen';
import ProjectTable from '@/components/project/ProjectTable';
import { useBranch } from '@/context/BranchsContext';
import { useProjectSearch } from '@/context/ProjectSearchContext';
import { Box, Button, FormControl, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// Enum to manage drawers
export enum DrawerProjects {
  NONE = '',
  SETTINGS = 'SETTINGS',
  DETAILS = 'DETAILS',
  SYNC_PROJECT_SPREADSHEET = 'SYNC_PROJECT_SPREADSHEET',
  COST_TRACKING = 'COST_TRACKING',
  GENERATE_REPORT = 'GENERATE_REPORT',
}

function Projects() {
  const { t } = useTranslation(['projects', 'common']);

  const { filters, setFilters } = useProjectSearch();
  const theme = useTheme();
  const { selectedBranch, setSelectedBranch } = useBranch();
  const [selectSearchProject, setSelectedSearchProject] = useState('');
  const [selectProjects, setSelectProjects] = useState<ProjectResponse[]>([]);
  const [selectOneProject, setSelectedOneProject] = useState<ProjectResponse | null>(null);

  const { data: branchs } = useBranchsQuery();
  const { data: projectsSubType } = useProjectSubTypeQuery();
  const [originProject, setOriginProject] = useState<ProjectResponse | null>(filters?.search || null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useProjectsQuery(
    {
      searchTerm: originProjectSearch,
      projectCode: null,
      bringClosed: true,
      projectSubType: '',
      branch: '',
      page: 0,
      size: 10,
    },
    { skip: originProjectSearch.length < 1 },
  );

  const [openDrawer, setOpenDrawer] = useState<DrawerProjects>(DrawerProjects.NONE);

  const [isDisabled, setIsDisabled] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleOpenDrawer = (drawer: DrawerProjects) => {
    setOpenDrawer(drawer);
  };
  const handleCloseDrawer = () => setOpenDrawer(DrawerProjects.NONE);

  const { data: projects, refetch } = useProjectsQuery({
    bringClosed: true,
    searchTerm: originProject?.code ? null : originProjectSearch,
    projectCode: originProject?.code || null,
    projectSubType: selectSearchProject || '',
    branch: selectedBranch || '',
    page,
    size: rowsPerPage,
  });
  const [exportProject] = useExportProjectMutation();
  const [generatingProjects, setGeneratingProjects] = useState<string[]>([]);

  const handleGenerateProjects = async (initPeriod: Dayjs, endPeriod: Dayjs, checked: boolean) => {
    const codesToProcess = selectProjects.map((p) => p.code).filter((code): code is string => code !== undefined);

    setGeneratingProjects((prev) => [...prev, ...codesToProcess]);

    handleCloseDrawer();
    setIsDisabled(true);

    try {
      await exportProject({
        projectCodes: codesToProcess,
        start: formatDateForBackend(initPeriod as Dayjs),
        end: formatDateForBackend(endPeriod as Dayjs),
        anonymizeLGPD: checked,
      } as GeneratedReportRequest).unwrap();

      toast.success(
        t('common:entry.sent.processing.success', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );

      const initialDates = selectProjects.reduce<Record<string, string | null>>((acc, proj) => {
        acc[proj.code as string] = proj.lastGenerationDate ?? null;
        return acc;
      }, {});

      let refetches = 0;
      const maxRefetches = 5;
      const baseInterval = 5000; // 5 seconds

      const pollForUpdates = async () => {
        const result = await refetch();
        const updatedProjects = result?.data?.content ?? [];

        setGeneratingProjects((prev) =>
          prev.filter((code) => {
            if (!codesToProcess.includes(code)) return true;

            const updated = updatedProjects.find((p: ProjectResponse) => p.code === code);
            const wasUpdated = updated?.lastGenerationDate && updated.lastGenerationDate !== initialDates[code];

            return !wasUpdated;
          }),
        );

        const allUpdated = codesToProcess.every((code) => {
          const updated = updatedProjects.find((p: ProjectResponse) => p.code === code);
          return updated?.lastGenerationDate && updated.lastGenerationDate !== initialDates[code];
        });

        refetches += 1;

        if (allUpdated || refetches >= maxRefetches) {
          setGeneratingProjects((prev) => prev.filter((code) => !codesToProcess.includes(code)));
        } else {
          const nextInterval = (refetches + 1) * baseInterval;
          setTimeout(pollForUpdates, nextInterval);
        }
      };

      setTimeout(pollForUpdates, baseInterval);
    } catch (error) {
      setGeneratingProjects((prev) => prev.filter((code) => !codesToProcess.includes(code)));

      toast.error(t('common:processing.failed'), {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsDisabled(false);
    }
  };

  const [syncPlanningReport] = useSyncPlanningReportMutation();
  const [syncingProjects, setSyncingProjects] = useState<string[]>([]);

  const handlePlanningReportSync = async (date: string, code: string) => {
    handleCloseDrawer();
    setSyncingProjects((prev) => [...prev, code]);

    setIsDisabled(true);
    try {
      const initialData = projects?.content.find((p) => p.lastPlanningImportDate === date);
      const initialLastImportDate = initialData?.lastPlanningImportDate;

      await toast.promise(
        syncPlanningReport(code).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('sync.started.success') as string,
          error: t('common:processing.failed') as string,
        },
        { position: toast.POSITION.TOP_RIGHT },
      );

      let refetches = 0;
      const maxRefetches = 5;
      const baseInterval = 4000;

      const pollForSyncUpdate = async () => {
        const result = await refetch();
        const updatedProject = result?.data?.content.find((p: ProjectResponse) => p.code === code);

        const isUpdated = updatedProject?.lastPlanningImportDate !== initialLastImportDate;
        refetches += 1;

        if (isUpdated || refetches >= maxRefetches) {
          setSyncingProjects((prev) => prev.filter((id) => id !== code));
        } else {
          const nextInterval = (refetches + 1) * baseInterval;
          setTimeout(pollForSyncUpdate, nextInterval);
        }
      };

      setTimeout(pollForSyncUpdate, baseInterval);
    } catch (error) {
      setSyncingProjects((prev) => prev.filter((id) => id !== code));
      toast.error(t('common:processing.failed') as string, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDisabled(false);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginRight: '15px',
      }}
    >
      <PeriodDetails />
      <PageTitle title={t('title')} />

      <Box
        sx={{
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box
          sx={{
            minWidth: theme.spacing(31),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography component="p" variant="fieldHeader">
            {t('common:project')}
          </Typography>
          <GenericAutoComplete
            value={originProject}
            onChange={(value) => {
              setOriginProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setOriginProjectSearch(value);
            }}
            options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
            placeholder={t('common:enter.project.name.or.code') as string}
            loading={isLoadingOrigin}
            sx={{ width: theme.spacing(80) }}
          />
        </Box>
        <Box sx={{ minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('common:branch')}
          </Typography>

          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              sx={{
                height: 32,
                fontSize: 14,
              }}
            >
              <MenuItem value="" sx={{ color: '#9e9e9e' }}>
                {t('common:all')}
              </MenuItem>
              {branchs &&
                branchs.map((item: BranchResponse) => (
                  <MenuItem key={item.code} value={item.code} sx={{ fontSize: 13 }}>
                    {item.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('project.type')}
          </Typography>

          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={selectSearchProject}
              onChange={(e) => setSelectedSearchProject(e.target.value)}
              sx={{
                height: 32,
                fontSize: 14,
              }}
            >
              <MenuItem value="" sx={{ color: '#9e9e9e' }}>
                {t('common:all')}
              </MenuItem>
              {projectsSubType &&
                projectsSubType.map((item: ProjectSubTypeResponse) => (
                  <MenuItem key={item.code} value={item.code} sx={{ fontSize: 13 }}>
                    {`${item.name}`}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => {
                handleOpenDrawer(DrawerProjects.GENERATE_REPORT);
              }}
              disabled={selectProjects.length === 0}
            >
              {t('generate.report')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <ProjectTable
        selectProjects={selectProjects}
        setSelectedProjects={setSelectProjects}
        setOpenDrawer={setOpenDrawer}
        setSelectedOneProject={setSelectedOneProject}
        selectOneProject={selectOneProject as ProjectResponse}
        data={projects as PaginationResponse<ProjectResponse>}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        syncingProjects={syncingProjects}
        handlePlanningReportSync={handlePlanningReportSync}
        generatingProjects={generatingProjects}
      />

      <LateralDrawer
        open={openDrawer === DrawerProjects.SETTINGS}
        setOpen={(v) => setOpenDrawer(v ? DrawerProjects.SETTINGS : DrawerProjects.NONE)}
        onClose={handleCloseDrawer}
        position="right"
        size="bigger"
      >
        <NewProjectsSettingsScreen
          setDrawerSendAttachmentEight={handleCloseDrawer}
          selectOneProject={selectOneProject}
        />
      </LateralDrawer>

      <LateralDrawer
        open={openDrawer === DrawerProjects.GENERATE_REPORT}
        setOpen={() => setOpenDrawer(DrawerProjects.GENERATE_REPORT)}
        onClose={handleCloseDrawer}
        position="right"
        size="bigger"
      >
        <GenerateReportScreen
          setDrawerSendAttachmentEight={handleCloseDrawer}
          selectProjects={selectProjects}
          setSelectProjects={setSelectProjects}
          handleGenerateProject={handleGenerateProjects}
          isDisabled={isDisabled}
        />
      </LateralDrawer>
    </div>
  );
}
export default Projects;
